import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import AutoCompleteField from '../../../components/general/fields/autoCompleteField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FieldText from '../../../components/general/fields/fieldText';
import DatePickerField from '../../../components/general/fields/datePickerField';
import StepperButtons from '../../../components/general/stepper/stepperButtons';
import { useDispatch } from 'react-redux';
import { types } from '../../../../redux/types';
import { asyncCitiesByText } from '../../../../services/general.service';
import FieldSelect from '../../../components/general/fields/fieldSelect';
import {
    asyncGetPerson,
    asyncPatchtPerson,
    asyncPostPerson,
} from '../../../../services/caseReport.service';
import dayjs from 'dayjs';
import { MasterMessage } from '../../../../masters/masterMessages';
import { SwalCustomClass } from '../../../components/general/toastSwal/swalCustomClass';

type Props = {
    stepsLength: number;
    identifications: any[];
    onInfoContact: (value: any) => void;
    onLoading: (value: boolean) => void;
    infoContact: any;
    citiesInfo: any;
    userLog: string;
    globalId: string;
    onFormikTouchedGlobalId: (value: boolean | undefined) => void;
    onFormikErrorsGlobalId: (value: string | undefined) => void;
    onFormikValueGlobalId: (value: string | undefined) => void;
};

const ContactInformation = ({
    stepsLength,
    identifications,
    onInfoContact,
    onLoading,
    infoContact,
    citiesInfo,
    userLog,
    globalId,
    onFormikTouchedGlobalId,
    onFormikErrorsGlobalId,
    onFormikValueGlobalId,
}: Props) => {
    const dispatch: any = useDispatch();
    const { messageError, messageUpdateError } = MasterMessage.caseReport;
    const [department, setDepartment] = useState<any>(undefined);
    const [searchCityName, setSearchCityName] = useState<any>(undefined);
    const [cities, setCities] = useState<
        { id: number; name: string; department: object }[]
    >([]);
    const [minor, setMinor] = useState<string>('');
    const [blurNumberId, setBlurNumberId] = useState<string>('');
    const [blurTypeId, setBlurTypeId] = useState<string>('');
    const [submit, setSubmit] = useState<boolean>(false);

    const validateTypeId = (typeId: any) => {
        return (
            identifications.filter((row) => row.id === typeId)[0]?.value || ''
        );
    };

    const formik = useFormik({
        initialValues: {
            typeId: infoContact?.typeId ? infoContact.typeId : undefined,
            numberId: infoContact?.numberId ? infoContact.numberId : '',
            birthDate: infoContact?.birthDate
                ? dayjs(infoContact.birthDate)
                : null,
            name: infoContact?.name ? infoContact.name : '',
            surname: infoContact?.surname ? infoContact.surname : '',
            address: infoContact?.address ? infoContact.address : '',
            departmentId: '',
            department: '',
            cityId: infoContact?.cityId ? infoContact?.cityId : '',
            email: infoContact?.email ? infoContact.email : '',
            cellphone: infoContact?.cellphone ? infoContact.cellphone : '',
            habeasData: infoContact?.habeasData
                ? infoContact.habeasData
                : undefined,
            idCredit: infoContact?.idCredit ? infoContact.idCredit : '',
            minor: infoContact?.minor ? infoContact.minor : undefined,
            tutorIdType: infoContact?.tutorIdType
                ? infoContact?.tutorIdType
                : undefined,
            tutorIdNumber: infoContact?.tutorIdNumber
                ? infoContact.tutorIdNumber.trimEnd()
                : '',
            tutorName: infoContact?.tutorName ? infoContact.tutorName : '',
            tutorSurname: infoContact?.tutorSurname
                ? infoContact.tutorSurname
                : '',
            tutorPhone: infoContact?.tutorPhone ? infoContact.tutorPhone : '',
            tutorCellphone: infoContact?.tutorCellphone
                ? infoContact.tutorCellphone
                : '',
            createdBy: userLog,
            globalId: '',
        },
        validationSchema: Yup.object({
            globalId: Yup.string()
                .required('El global id es obligatorio')
                .matches(
                    /^[A-Za-z0-9]{8}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{12}$/,
                    'Formato invalido'
                ),
            typeId: Yup.string().required(
                'El tipo de identificación es obligatorio'
            ),
            numberId: Yup.string()
                .required('La identificación es obligatoria')
                .when({
                    is: () =>
                        Boolean(validateTypeId(formik.values.typeId) !== '5'),
                    then: (schema) =>
                        schema.matches(
                            /^[0-9]*$/,
                            'La identificación solo puede contener números'
                        ),
                }),
            name: Yup.string()
                .required('El nombre es obligatorio')
                .test(
                    'NoNumbers',
                    'El nombre no puede contener números',
                    (value) => !/\d/.test(value)
                ),
            surname: Yup.string().when({
                is: () => Boolean(validateTypeId(formik.values.typeId) !== '3'),
                then: (schema) =>
                    schema
                        .required('El apellido es obligatorio')
                        .test(
                            'NoNumbers',
                            'El apellido no puede contener números',
                            (value) => !/\d/.test(value)
                        ),
            }),
            address: Yup.string().required('La dirección es obligatoria'),
            cityId: Yup.string().required('La ciudad es obligatoria'),
            email: Yup.string()
                .email('El correo es inválido')
                .required('El correo es obligatorio')
                .test('Validate Email', 'El correo es inválido', (value) => {
                    const re =
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return re.test(String(value).toLowerCase());
                }),
            cellphone: Yup.string()
                .required('El número de celular es obligatorio')
                .matches(
                    /^[0-9]*$/,
                    'El número de celular solo puede contener números'
                ),
            tutorIdType: Yup.string().when({
                is: () => Boolean(parseInt(minor) === 1),
                then: (schema) =>
                    schema.required(
                        'El tipo de identificación del tutor es obligatorio'
                    ),
            }),
            tutorIdNumber: Yup.string().when({
                is: () => Boolean(parseInt(minor) === 1),
                then: (schema) =>
                    schema
                        .required('La identificación del tutor es obligatorio')
                        .when('tutorIdType', {
                            is: (idNumberTutor: any) =>
                                Boolean(
                                    parseInt(validateTypeId(idNumberTutor)) !==
                                        5
                                ),
                            then: (schema2) =>
                                schema2.matches(
                                    /^[0-9]*$/,
                                    'La identificación solo puede contener números'
                                ),
                        }),
            }),
            tutorName: Yup.string().when({
                is: () => Boolean(parseInt(minor) === 1),
                then: (schema) =>
                    schema
                        .required('El nombre del tutor es obligatorio')
                        .test(
                            'NoNumbers',
                            'El nombre del tutor no puede contener números',
                            (value) => !/\d/.test(value)
                        ),
            }),
            tutorSurname: Yup.string().when({
                is: () => Boolean(parseInt(minor) === 1),
                then: (schema) =>
                    schema
                        .required('El apellido del tutor es obligatorio')
                        .test(
                            'NoNumbers',
                            'El apellido del tutor no puede contener números',
                            (value) => !/\d/.test(value)
                        ),
            }),
            tutorPhone: Yup.string().when({
                is: () => Boolean(parseInt(minor) === 1),
                then: (schema) =>
                    schema
                        .required('El número de telefono es obligatorio')
                        .matches(
                            /^[0-9]*$/,
                            'El número de telefono solo puede contener números'
                        ),
            }),
            tutorCellphone: Yup.string().when({
                is: () => Boolean(parseInt(minor) === 1),
                then: (schema) =>
                    schema
                        .required('El número de celular es obligatorio')
                        .matches(
                            /^[0-9]*$/,
                            'El número de celular solo puede contener números'
                        ),
            }),
        }),
        onSubmit: async (formContact) => {
            setSubmit(true);
            const data = { ...formContact };
            data.typeId = parseInt(validateTypeId(data.typeId));
            data.tutorIdType = formContact?.tutorIdType
                ? parseInt(validateTypeId(data.tutorIdType))
                : undefined;
            if (infoContact !== undefined && infoContact.name) {
                asyncPatchtPerson(infoContact.id, data)
                    .then((body: any) => {
                        if (body?.items !== undefined) {
                            dispatch({ type: types.nextStepper });
                            onInfoContact(body?.items);
                        } else {
                            SwalCustomClass.fire({
                                text: messageUpdateError,
                                icon: 'error',
                                iconColor: '#1d1d1d',
                            });
                        }
                    })
                    .finally(() => setSubmit(false))
                    .catch(() => setSubmit(false));
            } else {
                const body: any = await asyncPostPerson(data).finally(() =>
                    setSubmit(false)
                );
                if (body?.items !== undefined) {
                    const data = { ...body?.items };
                    data.departmentId = data.department.id;
                    data.departmentObject = data.department;
                    data.department = data.department.id;
                    data.cityId = data.city.id;
                    data.cityName = data.city.name;
                    data.cityObject = data.city;
                    dispatch({ type: types.nextStepper });
                    onInfoContact(data);
                } else {
                    SwalCustomClass.fire({
                        text: messageError,
                        icon: 'error',
                        iconColor: '#1d1d1d',
                    });
                }
            }
        },
    });

    useEffect(() => {
        formik.setFieldValue('globalId', globalId);
        onFormikTouchedGlobalId(formik.touched.globalId);
        onFormikErrorsGlobalId(formik.errors.globalId);
        onFormikValueGlobalId(formik.values.globalId);

        if (searchCityName) {
            searchCity(searchCityName.toUpperCase());
        }
        if (infoContact !== undefined) {
            setBlurTypeId(infoContact?.typeId);
            setBlurNumberId(infoContact?.numberId);
            if (citiesInfo.length > 0 && searchCityName === undefined) {
                setCities(citiesInfo);
                setDepartment(citiesInfo[0].department);
            }
            if (searchCityName === undefined && infoContact?.cityName) {
                if (infoContact?.cityObject) {
                    const data = { ...infoContact?.cityObject };
                    data.name = `${infoContact?.cityName} (${infoContact?.departmentObject.name})`;
                    setCities([data]);
                }
                formik.values.cityId = infoContact?.cityId;
                setDepartment(infoContact?.departmentObject);
            }
            if (infoContact?.minor >= 0) {
                setMinor(infoContact?.minor);
            }
        }
        formik.values.departmentId = department ? department?.id : '';
        let count = 0;
        if (
            blurNumberId !== '' &&
            blurTypeId !== '' &&
            infoContact === undefined &&
            formik.values.name === '' &&
            count === 0
        ) {
            setSubmit(true);
            asyncGetPerson(validateTypeId(blurTypeId), blurNumberId)
                .then((body: any) => {
                    if (body.items !== undefined) {
                        onLoading(true);
                        onInfoContact(body.items);
                    }
                })
                .finally(() => {
                    setSubmit(false);
                    count++;
                });
        } else if (
            infoContact &&
            ((blurTypeId !== '' &&
                infoContact?.typeId !== formik.values.typeId) ||
                (blurNumberId !== '' &&
                    infoContact?.numberId !== formik.values.numberId))
        ) {
            setSubmit(true);
            setBlurTypeId(formik.values.typeId);
            setBlurNumberId(formik.values.numberId);
            asyncGetPerson(validateTypeId(blurTypeId), blurNumberId)
                .then((body: any) => {
                    if (body.items !== undefined) {
                        onLoading(true);
                        onInfoContact(body.items);
                    } else if (body.message !== undefined) {
                        onInfoContact({
                            typeId: blurTypeId,
                            numberId: blurNumberId,
                        });
                        onLoading(true);
                    }
                })
                .finally(() => setSubmit(false));
        }
    }, [
        department,
        infoContact,
        blurNumberId,
        blurTypeId,
        citiesInfo,
        searchCityName,
        submit,
        globalId,
        formik.touched.globalId,
        formik.errors.globalId,
        formik.values.globalId,
    ]);

    const searchCity = (value: any) => {
        if (value.length === 1 || value.length === 3 || value.length === 5) {
            asyncCitiesByText(value.toUpperCase()).then((body: any) => {
                if (body?.length) setCities(body);
            });
        }
    };

    const validateCity = (value: any) => {
        formik.values.cityId = value;
        if (value) {
            const data = cities?.find((c) => c.id === value) || undefined;
            if (data !== undefined) {
                setDepartment(data.department);
            }
        } else {
            setDepartment(undefined);
        }
    };

    const validateMinor = (value: string) => {
        if (value) {
            formik.values.minor = value;
            setMinor(value);
        }
    };

    const options = [
        {
            id: 1,
            name: 'Si',
        },
        {
            id: 2,
            name: 'No',
        },
    ];

    return (
        <>
            <Box>
                <Grid
                    container
                    spacing={{ xs: 1.5, md: 1.5 }}
                    columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                >
                    <Grid item xs={12} md={6} lg={4}>
                        <AutoCompleteField
                            data={identifications}
                            label="Tipo de Identificacion"
                            name="typeId"
                            size="small"
                            required={true}
                            touched={formik.touched.typeId}
                            errors={formik.errors.typeId}
                            value={formik.values.typeId}
                            onChangeValue={(value) =>
                                formik.setFieldValue('typeId', value)
                            }
                            onBlur={(event) => setBlurTypeId(event.target.id)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <FieldText
                            label="Identificacion"
                            name="numberId"
                            size="small"
                            required={true}
                            touched={formik.touched.numberId}
                            errors={formik.errors.numberId}
                            value={formik.values.numberId}
                            onChange={formik.handleChange}
                            onBlur={(e) => setBlurNumberId(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <DatePickerField
                            label="Fecha Nacimiento"
                            name="birthDate"
                            size="small"
                            touched={formik.touched.birthDate}
                            errors={formik.errors.birthDate}
                            value={formik.values.birthDate}
                            onChange={(value) =>
                                formik.setFieldValue('birthDate', value, true)
                            }
                        />
                    </Grid>
                    {validateTypeId(formik.values.typeId) === '3' ? (
                        <>
                            <Grid item xs={12} md={12} lg={8}>
                                <FieldText
                                    label="Razón Social"
                                    name="name"
                                    size="small"
                                    required={true}
                                    touched={formik.touched.name}
                                    errors={formik.errors.name}
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} md={6} lg={4}>
                                <FieldText
                                    label="Nombres"
                                    name="name"
                                    size="small"
                                    required={true}
                                    touched={formik.touched.name}
                                    errors={formik.errors.name}
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <FieldText
                                    label="Apellidos"
                                    name="surname"
                                    size="small"
                                    required={true}
                                    touched={formik.touched.surname}
                                    errors={formik.errors.surname}
                                    value={formik.values.surname}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12} md={6} lg={4}>
                        <FieldText
                            label="Dirección"
                            name="address"
                            size="small"
                            required={true}
                            touched={formik.touched.address}
                            errors={formik.errors.address}
                            value={formik.values.address}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <AutoCompleteField
                            data={cities}
                            label="Ciudad"
                            name="cityId"
                            size="small"
                            required={true}
                            touched={formik.touched.cityId}
                            errors={formik.errors.cityId}
                            value={formik.values.cityId}
                            onChangeValue={(value) => validateCity(value)}
                            inputValueEnabled={true}
                            onInputValue={(value: any) =>
                                setSearchCityName(value)
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <FieldText
                            label="Departamento"
                            name="departmentId"
                            size="small"
                            required={true}
                            disabled
                            value={department?.name || ''}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <FieldText
                            label="Email"
                            name="email"
                            size="small"
                            type="email"
                            required={true}
                            touched={formik.touched.email}
                            errors={formik.errors.email}
                            value={formik.values.email}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <FieldText
                            label="Celular"
                            name="cellphone"
                            size="small"
                            required={true}
                            touched={formik.touched.cellphone}
                            errors={formik.errors.cellphone}
                            value={formik.values.cellphone}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                        <FieldSelect
                            data={options}
                            label="Habeas Data"
                            name="habeasData"
                            size="small"
                            touched={formik.touched.habeasData}
                            errors={formik.errors.habeasData}
                            value={formik.values.habeasData}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                        <FieldText
                            label="Identificador Crédito"
                            name="idCredit"
                            size="small"
                            touched={formik.touched.idCredit}
                            errors={formik.errors.idCredit}
                            value={formik.values.idCredit}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                        <FieldSelect
                            data={options}
                            label="Menor de Edad"
                            name="minor"
                            size="small"
                            touched={formik.touched.minor}
                            errors={formik.errors.minor}
                            value={formik.values.minor}
                            onChange={(e) => validateMinor(e.target.value)}
                        />
                    </Grid>
                    {parseInt(minor) === 1 && (
                        <>
                            <Grid item xs={12} md={6} lg={4}>
                                <AutoCompleteField
                                    data={identifications}
                                    label="Tipo de Identificacion (Tutor)"
                                    name="tutorIdType"
                                    size="small"
                                    required={true}
                                    touched={formik.touched.tutorIdType}
                                    errors={formik.errors.tutorIdType}
                                    value={formik.values.tutorIdType}
                                    onChangeValue={(value) =>
                                        formik.setFieldValue(
                                            'tutorIdType',
                                            value
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <FieldText
                                    label="Identificacion (Tutor)"
                                    name="tutorIdNumber"
                                    size="small"
                                    required={true}
                                    touched={formik.touched.tutorIdNumber}
                                    errors={formik.errors.tutorIdNumber}
                                    value={formik.values.tutorIdNumber}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <FieldText
                                    label="Nombres (Tutor)"
                                    name="tutorName"
                                    size="small"
                                    required={true}
                                    touched={formik.touched.tutorName}
                                    errors={formik.errors.tutorName}
                                    value={formik.values.tutorName}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <FieldText
                                    label="Apellidos (Tutor)"
                                    name="tutorSurname"
                                    size="small"
                                    required={true}
                                    touched={formik.touched.tutorSurname}
                                    errors={formik.errors.tutorSurname}
                                    value={formik.values.tutorSurname}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <FieldText
                                    label="Telefono (Tutor)"
                                    name="tutorPhone"
                                    size="small"
                                    required={true}
                                    touched={formik.touched.tutorPhone}
                                    errors={formik.errors.tutorPhone}
                                    value={formik.values.tutorPhone}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={4}>
                                <FieldText
                                    label="Celular (Tutor)"
                                    name="tutorCellphone"
                                    size="small"
                                    required={true}
                                    touched={formik.touched.tutorCellphone}
                                    errors={formik.errors.tutorCellphone}
                                    value={formik.values.tutorCellphone}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>
            <StepperButtons
                onClick={formik.handleSubmit}
                stepsLength={stepsLength}
                disabledNext={submit}
                loading={submit}
            />
        </>
    );
};

export default ContactInformation;
